import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Typography, Modal, message } from 'antd';

import { EMAIL_SCHEMA } from 'util/yup';
import FullReportRequestForm from './FullReportRequestForm';
import { SAMPLE_REPORT_URL } from 'components/FreeTrial/utils/consts';
import SuccessWithUpsellModal from 'components/CandidateComponents/CandidateExperienceV1/MySearchlightV2/components/SuccessModal';
import '../styles.less';
import './styles.less';

const FormSchema = Yup.object().shape({
  requesterName: Yup.string().required('Your name is required'),
  requesterRole: Yup.string().required('Role is required'),
  requesterEmail: EMAIL_SCHEMA.required('Work email is required'),
  requesterCompanyName: Yup.string().required('Company is required'),
  requesterLinkedin: Yup.string(),
  vanityLinkText: Yup.string().required(),
});

const FullReportModal = ({
  modalOpen,
  requestReportCallback,
  onCancelBtnClick,
  candidateFirstName,
  subTitle,
  vanityLinkText,
}) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const descriptions = React.useMemo(
    () => [
      'Over 20 questions',
      `Find out what makes ${candidateFirstName} a special candidate`,
      `Focus on ${candidateFirstName}'s soft skills`,
    ],
    [candidateFirstName]
  );
  const initialValues = {
    requesterName: '',
    requesterRole: '',
    requesterEmail: '',
    requesterCompanyName: '',
    requesterLinkedin: '',
    vanityLinkText,
  };
  const handleSubmit = useCallback(
    async (values, actions) => {
      try {
        await requestReportCallback(values);
        onCancelBtnClick(false);
        actions.resetForm();
        actions.setSubmitting(false);
        setShowSuccessModal(true);
      } catch (e) {
        message.error('Something went wrong!');
      }
    },
    [requestReportCallback, onCancelBtnClick]
  );

  return showSuccessModal ? (
    <SuccessWithUpsellModal
      successText="You have requested a Searchlight report!"
      nextSteps={[
        `${candidateFirstName} will receive a request from you and be able to authorize access`,
        'Once authorized, you will be notified and able to view the full report',
        `You will get rich insights to give you a holistic view of ${candidateFirstName}`,
      ]}
      isVisible={showSuccessModal}
      onClose={() => {
        setShowSuccessModal(false);
        onCancelBtnClick(false);
      }}
    />
  ) : (
    <Modal
      id="full-report-modal"
      width={800}
      closeIcon={<i className="fas fa-times" />}
      visible={modalOpen}
      onCancel={() => {
        onCancelBtnClick(false);
      }}
      footer={null}
    >
      <div className="tw-flex tw-flex-col tw-gap-4 flex">
        <div className="tw-flex-row tw-justify-between description-section align-items-start tw-flex">
          <div className="tw-flex-grow tw-min-w-0">
            <Typography.Title level={4} className="tw-text-lg">
              Get Access to {candidateFirstName}&rsquo;s Exclusive In-Depth Reference Report
            </Typography.Title>
            <ul className="descriptions tw-list-disc tw-text-sm tw-text-gray-700">
              {descriptions.map((description, index) => (
                <li key={index}>{description}</li>
              ))}
              <li key="sample_link">
                Preview a{' '}
                <a
                  href={SAMPLE_REPORT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="tw-text-gray-700 tw-underline"
                >
                  sample report
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="form-section tw-flex tw-flex-col tw-gap-4">
          <div className="tw-text-lg tw-font-semibold">We just need to confirm some details</div>
          {subTitle && <div className="tw-text-sm">{subTitle}</div>}
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={FormSchema}
          >
            {formProps => <FullReportRequestForm formProps={formProps} />}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

FullReportModal.propTypes = {
  modalOpen: PropTypes.bool,
  requestReportCallback: PropTypes.func.isRequired,
  onCancelBtnClick: PropTypes.func.isRequired,
  candidateFirstName: PropTypes.string.isRequired,
  vanityLinkText: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  onBackDropClick: PropTypes.func,
};

FullReportModal.defaultProps = {
  requestReportCallback: () => void 0,
  onCancelBtnClick: () => void 0,
};

export default FullReportModal;
